import { createContext, useCallback, useContext, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import type { ApolloError, ApolloQueryResult } from '@apollo/client';
import { GET_CURRENT_WORKLIST_REPORT } from 'modules/Apollo/queries';
import {
  WorklistItemStatus,
  type GetCurrentWorklistReportQuery,
  type GetCurrentWorklistReportQueryVariables,
} from 'generated/graphql';
import { useAsyncGlobalAction } from 'hooks/useGlobalAction';
import { logger } from 'modules/logger';
import { useMatch } from 'react-router-dom';
import { REPORTER_PREFIX_PATTERN } from 'utils/routePatterns';
import { AppRoutePath } from 'config/constants';
import { telemetryContextFor } from 'modules/Apollo/types';

export type ReportWorklistItem = GetCurrentWorklistReportQuery['worklistItem'];
export type LoadedReportWorklistItem = NonNullable<ReportWorklistItem>;
export type Report = NonNullable<LoadedReportWorklistItem['report']>;
export type ReportMacros = Report['macros'];
export type ReportTemplate = Report['template'];

type HookResult = {
  currentCaseId?: Nullable<string>;
  currentCaseReport?: ReportWorklistItem;
  refreshCaseReport?: () => Promise<void>;
  loadingCaseReport: boolean;
  loadingNewCaseReport: boolean;
  error?: ApolloError;
  refetch?: (
    variables?: Partial<Omit<GetCurrentWorklistReportQueryVariables, 'Record'>>
  ) => Promise<ApolloQueryResult<GetCurrentWorklistReportQuery>>;
  isAddendumLocked?: boolean;
};

/**
 * This hook is similar to `useCurrentCase` but only fetches data related to the current case report.
 */
export const useCurrentCaseReport = (): HookResult => {
  const currentCaseReport = useContext(CurrentCaseReport);

  if (!currentCaseReport) {
    throw new Error('useCurrentCaseReport must be used within a CurrentCaseReportProvider.');
  }

  return currentCaseReport;
};

const CurrentCaseReport = createContext<HookResult>({
  loadingNewCaseReport: true,
  loadingCaseReport: true,
});

export const CurrentCaseReportProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  // This hook is used by only the reporter and AI Sandbox tools, so extract the case ID from one or the other.
  const reporterWorklistId = useMatch(REPORTER_PREFIX_PATTERN)?.params?.worklistId;
  const aiSandboxWorklistId = useMatch(AppRoutePath.AI_SANDBOX)?.params?.worklistId;
  const patientJacketId = useMatch(AppRoutePath.PATIENT_JACKET)?.params?.smid;
  const currentCaseId = reporterWorklistId ?? aiSandboxWorklistId ?? patientJacketId;

  const {
    data: caseData,
    loading,
    error,
    refetch,
  } = useQuery<GetCurrentWorklistReportQuery, GetCurrentWorklistReportQueryVariables>(
    GET_CURRENT_WORKLIST_REPORT,
    {
      variables: currentCaseId == null ? undefined : { smid: currentCaseId },
      skip: currentCaseId == null,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      context: {
        telemetry: {
          ...APOLLO_QUERY_CONTEXT.telemetry,
          fetchPolicy: 'cache-and-network',
          nextFetchPolicy: 'cache-first',
        },
      },
    }
  );

  useEffect(() => {
    logger.info(`[Current Case Report]: currentCaseId: ${currentCaseId ?? 'none'}`);
  }, [currentCaseId]);

  const refetchWorklistItem = useCallback(async () => {
    if (currentCaseId != null) {
      logger.info(`[Current Case Report]: refetching case ${currentCaseId}`);

      await refetch({ smid: currentCaseId });
    }
  }, [currentCaseId, refetch]);

  const currentCaseReport = caseData?.worklistItem;

  const refreshCaseReport = useAsyncGlobalAction('use-current-case', refetchWorklistItem);

  const api = useMemo(
    () => ({
      currentCaseId,
      currentCaseReport,
      refreshCaseReport,
      refetch,
      loadingNewCaseReport: loading && currentCaseReport?.smid !== currentCaseId,
      loadingCaseReport: loading,
      error,
      isAddendumLocked:
        currentCaseReport?.status === WorklistItemStatus.InProgressAddendum &&
        currentCaseReport?.isActive,
    }),
    [currentCaseId, currentCaseReport, error, refreshCaseReport, refetch, loading]
  );

  return <CurrentCaseReport.Provider value={api}>{children}</CurrentCaseReport.Provider>;
};

CurrentCaseReportProvider.displayName = 'CurrentCaseReportProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(CurrentCaseReportProvider);
